/**
 * @fileoverview FileUpload Component
 *
 * Reusable file upload component with drag-and-drop support.
 * Integrates with Cloudinary for file storage.
 *
 * Features:
 * - Drag and drop file upload
 * - Click to browse files
 * - Image preview thumbnails
 * - PDF icon display
 * - Upload progress indicator
 * - Multiple file support
 * - Mobile camera access
 * - File validation
 * - Error handling
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../services/cloudinaryService
 */

import React, { useState, useRef, useCallback } from "react";
import {
  FaCloudUploadAlt,
  FaImage,
  FaFilePdf,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaCamera,
  FaSpinner,
} from "react-icons/fa";
import {
  uploadFile,
  validateFile,
  formatFileSize,
  getFileCategory,
  CLOUDINARY_SETTINGS,
} from "../services/cloudinaryService";
import "./FileUpload.css";

/**
 * FileUpload Component
 *
 * @param {Object} props - Component props
 * @param {Function} props.onUploadComplete - Callback when upload completes with file data
 * @param {Function} [props.onUploadError] - Callback when upload fails
 * @param {string[]} [props.allowedTypes] - Allowed file types: ['images'], ['documents'], or both
 * @param {boolean} [props.multiple] - Allow multiple file uploads
 * @param {number} [props.maxFiles] - Maximum number of files (if multiple)
 * @param {string} [props.folder] - Cloudinary subfolder for uploads
 * @param {string[]} [props.tags] - Tags to add to uploaded files
 * @param {Object[]} [props.existingFiles] - Pre-existing files to display
 * @param {Function} [props.onRemoveExisting] - Callback to remove existing file
 * @param {string} [props.label] - Label text for the upload area
 * @param {string} [props.hint] - Hint text below label
 * @param {boolean} [props.disabled] - Disable uploads
 * @param {boolean} [props.showCamera] - Show camera button on mobile
 * @param {string} [props.className] - Additional CSS class
 * @returns {JSX.Element} FileUpload component
 */
const FileUpload = ({
  onUploadComplete,
  onUploadError,
  allowedTypes = ["images", "documents"],
  multiple = false,
  maxFiles = 5,
  folder = "",
  tags = [],
  existingFiles = [],
  onRemoveExisting,
  label = "Upload Files",
  hint,
  disabled = false,
  showCamera = true,
  className = "",
}) => {
  // State
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]); // { id, file, progress, status, error, result }
  const [uploadedFiles, setUploadedFiles] = useState([]); // Successfully uploaded files

  // Refs
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const dragCounter = useRef(0);

  /**
   * Generate unique ID for tracking uploads
   */
  const generateId = () =>
    `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  /**
   * Get accepted file types string for input
   */
  const getAcceptString = () => {
    const accepts = [];
    if (allowedTypes.includes("images")) {
      accepts.push(
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/heic",
        "image/gif",
      );
    }
    if (allowedTypes.includes("documents")) {
      accepts.push("application/pdf");
    }
    return accepts.join(",");
  };

  /**
   * Check if more files can be added
   */
  const canAddMore = () => {
    if (!multiple) {
      return uploadedFiles.length === 0 && existingFiles.length === 0;
    }
    return uploadedFiles.length + existingFiles.length < maxFiles;
  };

  /**
   * Handle file selection
   */
  const handleFiles = useCallback(
    async (files) => {
      if (disabled) return;

      const fileArray = Array.from(files);

      // Limit files if multiple
      const filesToProcess = multiple
        ? fileArray.slice(
            0,
            maxFiles - uploadedFiles.length - existingFiles.length,
          )
        : [fileArray[0]];

      if (filesToProcess.length === 0) return;

      // Process each file
      for (const file of filesToProcess) {
        const uploadId = generateId();

        // Validate file
        const validation = validateFile(file, { allowedTypes });
        if (!validation.valid) {
          setUploadingFiles((prev) => [
            ...prev,
            {
              id: uploadId,
              file,
              progress: 0,
              status: "error",
              error: validation.error,
            },
          ]);

          if (onUploadError) {
            onUploadError({ file, error: validation.error });
          }
          continue;
        }

        // Add to uploading state
        setUploadingFiles((prev) => [
          ...prev,
          {
            id: uploadId,
            file,
            progress: 0,
            status: "uploading",
          },
        ]);

        try {
          // Upload file
          const result = await uploadFile(file, {
            folder,
            tags,
            allowedTypes,
            onProgress: (progress) => {
              setUploadingFiles((prev) =>
                prev.map((f) => (f.id === uploadId ? { ...f, progress } : f)),
              );
            },
          });

          // Mark as complete
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === uploadId ? { ...f, status: "complete", result } : f,
            ),
          );

          // Add to uploaded files
          const uploadedFile = {
            id: uploadId,
            url: result.url,
            thumbnail: result.thumbnail,
            filename: file.name,
            size: file.size,
            type: getFileCategory(file.type),
            publicId: result.publicId,
          };

          setUploadedFiles((prev) => [...prev, uploadedFile]);

          // Notify parent
          if (onUploadComplete) {
            onUploadComplete(uploadedFile);
          }

          // Remove from uploading after animation
          setTimeout(() => {
            setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadId));
          }, 1500);
        } catch (error) {
          // Mark as error
          setUploadingFiles((prev) =>
            prev.map((f) =>
              f.id === uploadId
                ? { ...f, status: "error", error: error.message }
                : f,
            ),
          );

          if (onUploadError) {
            onUploadError({ file, error: error.message });
          }
        }
      }
    },
    [
      disabled,
      multiple,
      maxFiles,
      uploadedFiles.length,
      existingFiles.length,
      allowedTypes,
      folder,
      tags,
      onUploadComplete,
      onUploadError,
    ],
  );

  /**
   * Handle drag events
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (disabled || !canAddMore()) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  /**
   * Detect mobile viewport (matches CSS breakpoint for mobile button stack).
   * Used to suppress the dropzone's click-to-browse on mobile, where the
   * dedicated Upload/Take Photo buttons are the intended entry points.
   */
  const isMobileViewport = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 768px)").matches;

  /**
   * Handle click to browse
   */
  const handleClick = () => {
    if (disabled || !canAddMore()) return;
    if (isMobileViewport()) return;
    fileInputRef.current?.click();
  };

  /**
   * Handle browse button (mobile)
   */
  const handleBrowseClick = (e) => {
    e.stopPropagation();
    if (disabled || !canAddMore()) return;
    fileInputRef.current?.click();
  };

  /**
   * Handle camera capture
   */
  const handleCameraClick = (e) => {
    e.stopPropagation();
    if (disabled || !canAddMore()) return;
    cameraInputRef.current?.click();
  };

  /**
   * Handle file input change
   */
  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  /**
   * Remove an uploaded file
   */
  const handleRemoveUploaded = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  /**
   * Remove a failed upload from the list
   */
  const handleDismissError = (uploadId) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadId));
  };

  /**
   * Render file preview
   */
  const renderFilePreview = (file, isExisting = false) => {
    const isImage = file.type === "image";
    const isPdf = file.type === "pdf";

    return (
      <div key={file.id || file.url} className="file-upload-preview">
        <div className="file-upload-preview-content">
          {isImage ? (
            <img
              src={file.thumbnail || file.url}
              alt={file.filename}
              className="file-upload-preview-image"
            />
          ) : isPdf ? (
            <div className="file-upload-preview-pdf">
              <FaFilePdf />
            </div>
          ) : (
            <div className="file-upload-preview-generic">
              <FaImage />
            </div>
          )}

          <button
            type="button"
            className="file-upload-preview-remove"
            onClick={() =>
              isExisting
                ? onRemoveExisting?.(file)
                : handleRemoveUploaded(file.id)
            }
            aria-label="Remove file"
          >
            <FaTimes />
          </button>
        </div>

        <div className="file-upload-preview-info">
          <span className="file-upload-preview-name" title={file.filename}>
            {file.filename}
          </span>
          {file.size && (
            <span className="file-upload-preview-size">
              {formatFileSize(file.size)}
            </span>
          )}
        </div>
      </div>
    );
  };

  /**
   * Render uploading file
   */
  const renderUploadingFile = (upload) => {
    const isError = upload.status === "error";
    const isComplete = upload.status === "complete";

    return (
      <div
        key={upload.id}
        className={`file-upload-progress-item ${upload.status}`}
      >
        <div className="file-upload-progress-content">
          {isError ? (
            <div className="file-upload-progress-icon error">
              <FaExclamationTriangle />
            </div>
          ) : isComplete ? (
            <div className="file-upload-progress-icon success">
              <FaCheck />
            </div>
          ) : (
            <div className="file-upload-progress-icon uploading">
              <FaSpinner className="spinning" />
            </div>
          )}

          <div className="file-upload-progress-info">
            <span className="file-upload-progress-name">
              {upload.file.name}
            </span>
            {isError ? (
              <span className="file-upload-progress-error">{upload.error}</span>
            ) : (
              <div className="file-upload-progress-bar-container">
                <div
                  className="file-upload-progress-bar"
                  style={{ width: `${upload.progress}%` }}
                />
              </div>
            )}
          </div>

          {isError && (
            <button
              type="button"
              className="file-upload-progress-dismiss"
              onClick={() => handleDismissError(upload.id)}
              aria-label="Dismiss error"
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>
    );
  };

  /**
   * Build hint text
   */
  const getHintText = () => {
    if (hint) return hint;

    const types = [];
    if (allowedTypes.includes("images")) types.push("images");
    if (allowedTypes.includes("documents")) types.push("PDFs");

    return `Drag & drop or click to upload ${types.join(" and ")}. Max ${
      CLOUDINARY_SETTINGS.maxFileSizeMB
    }MB.`;
  };

  // All files to display (existing + newly uploaded)
  const allFiles = [...existingFiles, ...uploadedFiles];
  const hasFiles = allFiles.length > 0 || uploadingFiles.length > 0;

  return (
    <div className={`file-upload-container ${className}`}>
      {/* Drop Zone */}
      <div
        className={`file-upload-dropzone ${isDragging ? "dragging" : ""} ${
          disabled ? "disabled" : ""
        } ${!canAddMore() ? "full" : ""}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={getAcceptString()}
          multiple={multiple}
          onChange={handleInputChange}
          className="file-upload-input"
          disabled={disabled}
        />

        {/* Camera input for mobile */}
        {showCamera && allowedTypes.includes("images") && (
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleInputChange}
            className="file-upload-input"
            disabled={disabled}
          />
        )}

        <div className="file-upload-content">
          <div className="file-upload-icon">
            <FaCloudUploadAlt />
          </div>

          <div className="file-upload-text">
            <span className="file-upload-label">{label}</span>
            <span className="file-upload-hint">{getHintText()}</span>
          </div>

          {/* Action buttons for mobile (browse + camera) */}
          {canAddMore() && (
            <div className="file-upload-mobile-actions">
              <button
                type="button"
                className="file-upload-mobile-btn file-upload-mobile-btn-primary"
                onClick={handleBrowseClick}
                aria-label="Browse photos"
              >
                <FaImage />
                <span>Browse Photos</span>
              </button>
              {showCamera && allowedTypes.includes("images") && (
                <button
                  type="button"
                  className="file-upload-mobile-btn file-upload-mobile-btn-secondary"
                  onClick={handleCameraClick}
                  aria-label="Take photo"
                >
                  <FaCamera />
                  <span>Take Photo</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Uploading Files */}
      {uploadingFiles.length > 0 && (
        <div className="file-upload-progress-list">
          {uploadingFiles.map(renderUploadingFile)}
        </div>
      )}

      {/* Uploaded Files Preview */}
      {allFiles.length > 0 && (
        <div className="file-upload-preview-list">
          {existingFiles.map((file) => renderFilePreview(file, true))}
          {uploadedFiles.map((file) => renderFilePreview(file, false))}
        </div>
      )}

      {/* File count indicator for multiple */}
      {multiple && (
        <div className="file-upload-count">
          {allFiles.length} / {maxFiles} files
        </div>
      )}
    </div>
  );
};

export default FileUpload;
